import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { suggestionsService, messagesService, authService, auth, usersService, newsletterService } from '../lib/firebase';
import { 
  LogIn, 
  LogOut, 
  CheckCircle2, 
  Clock, 
  Trash2, 
  Filter, 
  MessageSquare,
  ShieldCheck,
  ChevronRight,
  TrendingUp,
  Layout,
  Mail,
  Users,
  Bell
} from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import { cn } from '../lib/utils';

export default function AdminPanel({ userEmail, setToast }: { userEmail: string, setToast: (toast: any) => void }) {
  const [user, setUser] = useState<any>(null);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [usersList, setUsersList] = useState<any[]>([]);
  const [subscribers, setSubscribers] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'suggestions' | 'messages' | 'users' | 'newsletter'>('suggestions');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState<{ message: string; subMessage?: string; onConfirm: () => void } | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user && user.email === userEmail) {
      const unsubSuggestions = suggestionsService.subscribeToSuggestions(setSuggestions);
      const unsubMessages = messagesService.subscribeToMessages(setMessages);
      const unsubUsers = usersService.subscribeToUsers(setUsersList);
      const unsubNewsletter = newsletterService.subscribeToNewsletter(setSubscribers);
      return () => {
        unsubSuggestions();
        unsubMessages();
        unsubUsers();
        unsubNewsletter();
      };
    }
  }, [user, userEmail]);

  const handleStatusUpdate = async (id: string, status: string) => {
    await suggestionsService.updateStatus(id, status);
  };

  const handleDeleteSuggestion = (id: string) => {
    setConfirmDialog({
      message: 'Delete this suggestion?',
      subMessage: 'Are you sure you want to delete this suggestion? This action cannot be undone.',
      onConfirm: async () => {
        try {
          console.log('Attempting to delete suggestion:', id);
          await suggestionsService.deleteSuggestion(id);
          setToast({ message: 'Suggestion deleted', type: 'success' });
        } catch (error: any) {
          console.error('Delete error details:', error);
          let errorMsg = 'Error deleting suggestion';
          const currentUserEmail = auth.currentUser?.email;
          try {
            const parsed = JSON.parse(error.message);
            errorMsg = `Permission Denied (${currentUserEmail}): ${parsed.operationType} on ${parsed.path}`;
          } catch(e) {
            errorMsg = `${error.message || String(error)} (User: ${currentUserEmail})`;
          }
          setToast({ message: errorMsg, type: 'error' });
        }
        setConfirmDialog(null);
      }
    });
  };

  const handleDeleteMessage = (id: string) => {
    setConfirmDialog({
      message: 'Delete this message?',
      subMessage: 'Are you sure you want to delete this message? This action cannot be undone.',
      onConfirm: async () => {
        try {
          console.log('Attempting to delete message:', id);
          await messagesService.deleteMessage(id);
          setToast({ message: 'Message deleted', type: 'success' });
        } catch (error: any) {
          console.error('Delete error details:', error);
          let errorMsg = 'Error deleting message';
          const currentUserEmail = auth.currentUser?.email;
          try {
            const parsed = JSON.parse(error.message);
            errorMsg = `Permission Denied (${currentUserEmail}): ${parsed.operationType} on ${parsed.path}`;
          } catch(e) {
            errorMsg = `${error.message || String(error)} (User: ${currentUserEmail})`;
          }
          setToast({ message: errorMsg, type: 'error' });
        }
        setConfirmDialog(null);
      }
    });
  };

  const filteredSuggestions = suggestions.filter(s => 
    filter === 'all' ? true : s.status === filter
  );

  const stats = {
    total: suggestions.length,
    pending: suggestions.filter(s => s.status === 'pending').length,
    done: suggestions.filter(s => s.status === 'done').length
  };

  if (loading) return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (!user || user.email !== userEmail) {
    return (
      <div className="p-10 max-w-4xl mx-auto text-center">
        <div className="w-20 h-20 bg-rose-50 dark:bg-rose-900/10 text-rose-500 rounded-3xl flex items-center justify-center mx-auto mb-6">
          <ShieldCheck className="w-10 h-10" />
        </div>
        <h2 className="text-3xl font-bold mb-4 dark:text-white font-sans tracking-tight">Restricted Access</h2>
        <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-md mx-auto">
          This area is reserved for the platform administrator. Please sign in with the authorized account.
        </p>
        <button 
          onClick={() => authService.signIn()}
          className="bg-accent text-white px-8 py-3 rounded-2xl font-bold flex items-center gap-2 mx-auto hover:bg-accent/90 transition-all shadow-lg shadow-accent/20"
        >
          <LogIn className="w-5 h-5" /> Sign in with Google
        </button>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 md:p-10 max-w-[1400px] mx-auto w-full"
    >
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-500/10 text-indigo-500 rounded-2xl flex items-center justify-center">
            <Layout className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight dark:text-white font-sans">Admin Console</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm flex flex-col md:flex-row md:items-center md:gap-4">
              <span>Signed in: <span className="font-bold text-accent">{user?.email}</span></span>
              <span className="text-[10px] opacity-50 font-mono">UID: {user?.uid}</span>
            </p>
          </div>
        </div>
        <button 
          onClick={() => authService.signOut()}
          className="text-slate-500 hover:text-rose-500 transition-colors flex items-center gap-2 font-bold text-sm bg-slate-50 dark:bg-slate-900 px-6 py-3 rounded-xl border border-line dark:border-slate-800"
        >
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </header>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
        <div className="bento-card p-6 group relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Signals</p>
            <p className="text-3xl font-bold font-sans tracking-tight">{stats.total + messages.length}</p>
          </div>
          <TrendingUp className="absolute -right-4 -bottom-4 w-20 h-20 text-slate-100 dark:text-slate-800 transition-transform group-hover:scale-110 rotate-[-15deg] select-none pointer-events-none" />
        </div>
        <div className="bento-card p-6 group relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-[10px] font-bold text-amber-400 uppercase tracking-widest mb-1">Pending Tools</p>
            <p className="text-3xl font-bold font-sans tracking-tight text-amber-500">{stats.pending}</p>
          </div>
          <Clock className="absolute -right-4 -bottom-4 w-20 h-20 text-amber-100 dark:text-amber-900/20 transition-transform group-hover:scale-110 rotate-[-15deg] select-none pointer-events-none" />
        </div>
        <div className="bento-card p-6 group relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-[10px] font-bold text-rose-400 uppercase tracking-widest mb-1">Unread Mail</p>
            <p className="text-3xl font-bold font-sans tracking-tight text-rose-500">{messages.length}</p>
          </div>
          <Mail className="absolute -right-4 -bottom-4 w-20 h-20 text-rose-100 dark:text-rose-900/20 transition-transform group-hover:scale-110 rotate-[-15deg] select-none pointer-events-none" />
        </div>
        <div className="bento-card p-6 group relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest mb-1">Done</p>
            <p className="text-3xl font-bold font-sans tracking-tight text-emerald-500">{stats.done}</p>
          </div>
          <CheckCircle2 className="absolute -right-4 -bottom-4 w-20 h-20 text-emerald-100 dark:text-emerald-900/20 transition-transform group-hover:scale-110 rotate-[-15deg] select-none pointer-events-none" />
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mb-8">
        <button 
          onClick={() => setActiveTab('suggestions')}
          className={cn(
            "px-8 py-3 rounded-2xl font-bold text-sm transition-all flex items-center gap-2",
            activeTab === 'suggestions' ? "bg-slate-900 text-white shadow-xl" : "bg-white dark:bg-slate-900 border border-line dark:border-slate-800 text-slate-400"
          )}
        >
          <MessageSquare className="w-4 h-4" /> Suggestions
        </button>
        <button 
          onClick={() => setActiveTab('messages')}
          className={cn(
            "px-8 py-3 rounded-2xl font-bold text-sm transition-all flex items-center gap-2",
            activeTab === 'messages' ? "bg-slate-900 text-white shadow-xl" : "bg-white dark:bg-slate-900 border border-line dark:border-slate-800 text-slate-400"
          )}
        >
          <Mail className="w-4 h-4" /> Contact Messages
        </button>
        <button 
          onClick={() => setActiveTab('users')}
          className={cn(
            "px-8 py-3 rounded-2xl font-bold text-sm transition-all flex items-center gap-2",
            activeTab === 'users' ? "bg-slate-900 text-white shadow-xl" : "bg-white dark:bg-slate-900 border border-line dark:border-slate-800 text-slate-400"
          )}
        >
          <Users className="w-4 h-4" /> User Base
        </button>
        <button 
          onClick={() => setActiveTab('newsletter')}
          className={cn(
            "px-8 py-3 rounded-2xl font-bold text-sm transition-all flex items-center gap-2",
            activeTab === 'newsletter' ? "bg-slate-900 text-white shadow-xl" : "bg-white dark:bg-slate-900 border border-line dark:border-slate-800 text-slate-400"
          )}
        >
          <Bell className="w-4 h-4" /> Newsletter
        </button>
      </div>

      {/* Main Content Area */}
      <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-line dark:border-slate-800 p-2 overflow-hidden shadow-sm">
        {activeTab === 'suggestions' ? (
          <>
            <div className="p-6 border-b border-line dark:border-slate-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-slate-400" />
                <h2 className="font-bold text-lg">Suggestion Library</h2>
              </div>
              
              <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 p-1.5 rounded-xl">
                {['all', 'pending', 'implementing', 'done'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-xs font-bold capitalize transition-all",
                      filter === f 
                        ? "bg-white dark:bg-slate-900 text-accent shadow-sm" 
                        : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    )}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div className="divide-y divide-line dark:divide-slate-800 max-h-[600px] overflow-y-auto custom-scrollbar">
              <AnimatePresence mode="popLayout">
                {filteredSuggestions.length > 0 ? filteredSuggestions.map((s) => (
                  <motion.div 
                    key={s.id}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="p-6 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group"
                  >
                    <div className="flex flex-col md:flex-row gap-6 justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={cn(
                            "px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest",
                            s.status === 'pending' ? "bg-amber-50 text-amber-600 dark:bg-amber-900/20" :
                            s.status === 'implementing' ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20" :
                            "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20"
                          )}>
                            {s.status}
                          </span>
                          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                            {s.createdAt?.toDate ? new Date(s.createdAt.toDate()).toLocaleDateString() : 'Just now'}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold mb-1 dark:text-white group-hover:text-accent transition-colors">{s.toolName}</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl leading-relaxed">{s.description}</p>
                        {s.userEmail && (
                          <p className="mt-2 text-xs font-medium text-slate-400 flex items-center gap-1">
                            From: <span className="text-slate-600 dark:text-slate-200">{s.userEmail}</span>
                          </p>
                        )}
                      </div>

                                <div className="flex flex-wrap gap-2 shrink-0">
                        {s.status === 'pending' && (
                          <button 
                            onClick={() => handleStatusUpdate(s.id, 'implementing')}
                            className="p-2.5 bg-blue-50 text-blue-600 dark:bg-blue-900/20 rounded-xl hover:scale-105 active:scale-95 transition-all"
                            title="Start implementing"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        )}
                        {s.status === 'implementing' && (
                          <button 
                            onClick={() => handleStatusUpdate(s.id, 'done')}
                            className="p-2.5 bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 rounded-xl hover:scale-105 active:scale-95 transition-all"
                            title="Mark as completed"
                          >
                            <CheckCircle2 className="w-5 h-5" />
                          </button>
                        )}
                        <button 
                          onClick={() => handleDeleteSuggestion(s.id)}
                          className="p-2.5 bg-rose-50 text-rose-600 dark:bg-rose-900/20 rounded-xl hover:scale-105 active:scale-95 transition-all md:opacity-0 md:group-hover:opacity-100 opacity-100"
                          title="Archive suggestion"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )) : (
                  <div className="p-20 text-center">
                    <p className="text-slate-400 font-medium italic">No suggestions found in this category.</p>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </>
        ) : activeTab === 'messages' ? (
          <>
            <div className="p-6 border-b border-line dark:border-slate-800">
               <h2 className="font-bold text-lg flex items-center gap-2">
                 <Mail className="w-5 h-5 text-rose-500" />
                 Transmission Log
               </h2>
            </div>
            <div className="divide-y divide-line dark:divide-slate-800 max-h-[600px] overflow-y-auto custom-scrollbar">
               {messages.length > 0 ? messages.map((m) => (
                 <div key={m.id} className="p-8 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-4">
                       <div>
                          <div className="flex items-center gap-3 mb-1">
                             <h3 className="font-bold text-lg dark:text-white">{m.subject}</h3>
                             <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                {m.createdAt?.toDate ? new Date(m.createdAt.toDate()).toLocaleString() : 'Just now'}
                             </span>
                          </div>
                          <p className="text-sm font-bold text-accent">{m.name} &lt;{m.email}&gt;</p>
                       </div>
                       <button 
                          onClick={() => handleDeleteMessage(m.id)}
                          className="p-3 bg-rose-50 dark:bg-rose-900/20 text-rose-500 rounded-xl hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-all hover:scale-110 active:scale-95"
                          title="Delete message"
                       >
                          <Trash2 className="w-5 h-5" />
                       </button>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed uppercase-none bg-slate-50 dark:bg-slate-900/50 p-6 rounded-2xl border border-line dark:border-slate-800">
                       {m.message}
                    </p>
                 </div>
               )) : (
                 <div className="p-20 text-center">
                    <Mail className="w-12 h-12 text-slate-200 dark:text-slate-800 mx-auto mb-4" />
                    <p className="text-slate-400 font-medium italic">No contact transmissions yet.</p>
                 </div>
               )}
            </div>
          </>
        ) : activeTab === 'users' ? (
          <>
            <div className="p-6 border-b border-line dark:border-slate-800">
               <h2 className="font-bold text-lg flex items-center gap-2">
                 <Users className="w-5 h-5 text-indigo-500" />
                 User Activity Log
               </h2>
            </div>
            <div className="max-h-[600px] overflow-y-auto custom-scrollbar">
              {usersList.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/50 border-b border-line dark:border-slate-800">
                      <tr>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-400">User</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Activity</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Last Login</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line dark:divide-slate-800">
                      {usersList.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                          <td className="p-6">
                            <div className="flex items-center gap-3">
                              <img src={u.photoURL} alt="" className="w-8 h-8 rounded-full border border-line dark:border-slate-700" />
                              <div>
                                <p className="text-sm font-bold dark:text-white leading-none mb-1">{u.displayName}</p>
                                <p className="text-[10px] text-slate-400">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-6">
                            <span className="bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest">Active</span>
                          </td>
                          <td className="p-6">
                            <div className="flex flex-col gap-1">
                              <p className="text-[10px] font-medium text-slate-500 uppercase tracking-widest">Registered</p>
                              <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                {u.createdAt?.toDate ? new Date(u.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                          </td>
                          <td className="p-6 text-right">
                            <p className="text-xs font-mono font-bold text-slate-600 dark:text-slate-400">
                              {u.lastLogin?.toDate ? new Date(u.lastLogin.toDate()).toLocaleString() : 'Just now'}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-20 text-center">
                  <p className="text-slate-400 font-medium italic">No users recorded yet.</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="p-6 border-b border-line dark:border-slate-800 flex justify-between items-center">
               <h2 className="font-bold text-lg flex items-center gap-2">
                 <Bell className="w-5 h-5 text-amber-500" />
                 Newsletter Subscribers
               </h2>
               <div className="bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full text-[10px] font-bold text-slate-500">
                 {subscribers.length} Emails
               </div>
            </div>
            <div className="max-h-[600px] overflow-y-auto custom-scrollbar">
              {subscribers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/50 border-b border-line dark:border-slate-800">
                      <tr>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Email Address</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-400">Subscribed On</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line dark:divide-slate-800">
                      {subscribers.map((s) => (
                        <tr key={s.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                          <td className="p-6">
                            <p className="text-sm font-bold dark:text-white">{s.email}</p>
                          </td>
                          <td className="p-6">
                            <p className="text-xs text-slate-500">
                              {s.subscribedAt?.toDate ? new Date(s.subscribedAt.toDate()).toLocaleString() : 'Recent'}
                            </p>
                          </td>
                          <td className="p-6 text-right">
                            <button 
                              onClick={() => {
                                setConfirmDialog({
                                  message: 'Remove this subscriber?',
                                  subMessage: 'Are you sure you want to remove this subscriber from the list?',
                                  onConfirm: async () => {
                                    try {
                                      console.log('Attempting to delete subscriber:', s.email);
                                      await newsletterService.unsubscribe(s.email);
                                      setToast({ message: 'Subscriber removed', type: 'success' });
                                    } catch (error: any) {
                                      console.error('Delete error details:', error);
                                      let errorMsg = 'Error removing subscriber';
                                      const currentUserEmail = auth.currentUser?.email;
                                      try {
                                        const parsed = JSON.parse(error.message);
                                        errorMsg = `Permission Denied (${currentUserEmail}): ${parsed.operationType} on ${parsed.path}`;
                                      } catch(e) {
                                        errorMsg = `${error.message || String(error)} (User: ${currentUserEmail})`;
                                      }
                                      setToast({ message: errorMsg, type: 'error' });
                                    }
                                    setConfirmDialog(null);
                                  }
                                });
                              }}
                              className="p-3 bg-rose-50 dark:bg-rose-900/20 text-rose-500 rounded-xl hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-all hover:scale-110 active:scale-95"
                              title="Delete subscriber"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-20 text-center">
                  <p className="text-slate-400 font-medium italic">No newsletter subscribers yet.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Confirm Modal */}
      {confirmDialog && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setConfirmDialog(null)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <motion.div 
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-2xl shadow-rose-900/20 border border-line dark:border-slate-800"
          >
            <div className="w-12 h-12 bg-rose-100 dark:bg-rose-900/30 text-rose-500 rounded-2xl flex items-center justify-center mb-6">
              <Trash2 className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold tracking-tight mb-2">{confirmDialog.message}</h3>
            {confirmDialog.subMessage && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">{confirmDialog.subMessage}</p>
            )}
            <div className="flex gap-3">
              <button 
                onClick={() => setConfirmDialog(null)}
                className="flex-1 py-3 px-4 rounded-xl font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDialog.onConfirm}
                className="flex-1 py-3 px-4 rounded-xl font-bold bg-rose-500 text-white hover:bg-rose-600 transition-colors shadow-lg shadow-rose-500/20"
              >
                Confirm
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
